// キャンバス名付きのリンク文字列（Markdown 1 行と、アンカー 1 個だけの HTML 断片）を
// 組み立てる純粋関数。
// DOM・クリップボード・画面状態には一切触れない。副作用はイベント境界
// （features/canvas/controls/canvas-link-copy-button.tsx）だけが持つ。HTML 断片も DOM API を
// 使わず、文字列として組み立ててこの契約を保つ。

/** リンクのラベル末尾へ付けるサービス名。共有先でリンクの出どころが分かるようにする。 */
export const CANVAS_LINK_LABEL_SUFFIX = "｜Connect Canvas";

/**
 * 改行類（CRLF / LF / CR / 垂直タブ / 改ページ / NEL / Unicode の行区切り Zl / 段落区切り Zp）。
 * ラベルを 1 行へ畳むために使う。
 *
 * キャンバス名は表示時に normalizeCanvasTitle で trim されるだけで、内部の改行は落ちない。
 * MCP や REST API から設定された名前には改行が混じり得るため、ここで空白へ畳む。
 * U+000B / U+000C / U+0085 は Markdown の行末ではないが Unicode の改行制御であり、
 * 貼り付け先の表示や行分割処理では改行として扱われるため、同じ走査の対象に含める。
 */
const LINE_BREAK_PATTERN = /\r\n|[\n\r\v\f\u0085\p{Zl}\p{Zp}]/gu;

/**
 * CommonMark の ASCII punctuation（`!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~`）。
 * backslash escape の対象であり、ここへ `\` を前置すると文字そのものとして描画される。
 */
const ASCII_PUNCTUATION_PATTERN = /[!-/:-@[-`{-~]/gu;

/**
 * Markdown のリンク宛先（`(...)` の中）で解釈を変え得る文字。
 * 空白類はリンクタイトルの開始位置になり、括弧は宛先の早期終了、`<` `>` はポインタ形式の
 * 宛先、backslash は escape、backtick はコードスパンの開始になり得る。
 */
const DESTINATION_UNSAFE_PATTERN = /[\s"'()<>\\`]/gu;

/**
 * HTML のテキスト部（アンカーの表示文言）で実体参照化する文字と、その置換先。
 * 1 回の走査で 1 文字ずつ置き換えるため、`&` を先に処理するといった順序依存が生まれない。
 */
const HTML_TEXT_REFERENCES: Readonly<Record<string, string>> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
};

/** HTML の属性値で実体参照化する文字。テキスト部の対象に、属性値を閉じてしまう `"` を加える。 */
const HTML_ATTRIBUTE_REFERENCES: Readonly<Record<string, string>> = {
  ...HTML_TEXT_REFERENCES,
  '"': "&quot;",
};

/** HTML のテキスト部で実体参照化する文字の集合。 */
const HTML_TEXT_PATTERN = /[&<>]/gu;

/** HTML の属性値で実体参照化する文字の集合。 */
const HTML_ATTRIBUTE_PATTERN = /[&<>"]/gu;

export interface CanvasLinkInput {
  /**
   * 表示中の絶対 URL（`window.location.href` 相当）。
   * 表示位置とズームのクエリ（`cx` / `cy` / `z`）も含めてそのまま宛先に使い、
   * この関数はクエリやフラグメントを取り除かない。
   */
  readonly href: string;
  /**
   * 表示用に正規化済みのキャンバス名（`normalizeCanvasTitle` の結果）。
   * 空文字や旧既定値の読み替えは呼び出し元の責務であり、この関数では行わない。
   */
  readonly title: string;
}

/** クリップボードへ 1 回で書き込む 2 形式。どちらも同じ表示文言・同じ宛先から作る。 */
export interface CanvasLinkClipboardPayload {
  /**
   * `text/html` へ入れるアンカー 1 個だけの断片。
   * 宛先が `http` / `https` 以外のときは空文字になり、この場合は `text/html` を書き込まない。
   */
  readonly html: string;
  /** `text/plain` へ入れる Markdown 1 行。 */
  readonly markdown: string;
}

/**
 * 改行類を半角空白 1 個へ正規化する。
 * Markdown のリンクラベルは 1 行で完結させる必要があるため、表示名の改行だけを畳む。
 * 日本語・絵文字・全角記号などの表示文字は、そのまま保持する。
 */
function normalizeLabelLineBreaks(text: string): string {
  return text.replace(LINE_BREAK_PATTERN, " ");
}

/**
 * ラベル内の ASCII punctuation を、CommonMark の backslash escape ですべて打ち消す。
 * `[` `]` だけでなく backtick・`*`・`_`・`<` `>` `&` 等も対象にすることで、リンクの早期終了、
 * 強調の混入、生 HTML の注入を防ぐ。元から含まれる `\` も、この 1 回の走査だけで二重化される。
 */
function escapeMarkdownLabel(text: string): string {
  return text.replace(ASCII_PUNCTUATION_PATTERN, "\\$&");
}

/**
 * 1 文字を percent-encoding へ変換する。
 * `encodeURIComponent` が素通しする ASCII 記号（`!'()*-._~`）も、必ず `%XX` 形式にする。
 */
function percentEncodeChar(char: string): string {
  const encoded = encodeURIComponent(char);
  if (encoded !== char) {
    // 非 ASCII を含め、UTF-8 の percent-encoding は encodeURIComponent が正しく行う。
    return encoded;
  }
  // ここへ来るのは encodeURIComponent が変換しない ASCII 記号だけなので、常に 1 バイトになる。
  return `%${(char.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(2, "0")}`;
}

/**
 * Markdown の宛先を壊す文字を percent-encode する。
 * 対象は path / query / hash に現れる記号だけで、scheme・host・区切りの `/` `?` `#` `&` `=` は
 * 変換しない。したがって、この変換で参照先のキャンバス・座標・ズームは変わらない。
 */
function encodeMarkdownDestination(href: string): string {
  return href.replace(DESTINATION_UNSAFE_PATTERN, percentEncodeChar);
}

/**
 * HTML のテキスト部（アンカーの表示文言）を実体参照へ変換する。
 * 1 回の走査で置き換えるため、置換で生まれた実体参照をもう一度変換することがない。
 */
function escapeHtmlText(text: string): string {
  return text.replace(
    HTML_TEXT_PATTERN,
    (character) => HTML_TEXT_REFERENCES[character] ?? character
  );
}

/** HTML の属性値（アンカーの `href`）を実体参照へ変換する。 */
function escapeHtmlAttribute(value: string): string {
  return value.replace(
    HTML_ATTRIBUTE_PATTERN,
    (character) => HTML_ATTRIBUTE_REFERENCES[character] ?? character
  );
}

/**
 * 2 つの形式が必ず同じ値から作られるようにするための中間値。
 * この段階では、Markdown 用の escape も HTML 用の escape もまだ適用しない。
 */
interface CanvasLinkParts {
  /** 宛先 URL。表示中の URL をそのまま使う（クエリ・フラグメントを落とさない）。 */
  readonly destination: string;
  /** 表示文言（escape 前の生の文字列）。 */
  readonly label: string;
}

/** 表示中の URL とキャンバス名から、両形式が共有する中間値を確定する。 */
function buildCanvasLinkParts({ href, title }: CanvasLinkInput): CanvasLinkParts {
  return {
    destination: href,
    label: `${normalizeLabelLineBreaks(title)}${CANVAS_LINK_LABEL_SUFFIX}`,
  };
}

/** 中間値から Markdown リンク 1 行を組み立てる。 */
function formatMarkdownLink({ destination, label }: CanvasLinkParts): string {
  return `[${escapeMarkdownLabel(label)}](${encodeMarkdownDestination(destination)})`;
}

/**
 * 中間値からアンカー 1 個だけの HTML 断片を組み立てる。
 *
 * Markdown 用の escape を HTML へ流用してはいけない。`escapeMarkdownLabel` は ASCII punctuation へ
 * `\` を前置するため、そのまま HTML へ入れると表示文言に `\` が現れる。
 * `encodeMarkdownDestination` も Markdown の宛先を壊す記号だけを対象にした変換であり、
 * HTML 属性の要件とは異なる。`text/html` は貼り付け先で URL が展開表示されることがあるため、
 * 宛先は percent-encode せず、実体参照化だけを行った生の表記を入れる。
 *
 * @returns アンカー 1 個の断片。宛先が `http` / `https` 以外、または URL として解釈できない場合は空文字
 */
function formatAnchorHtml({ destination, label }: CanvasLinkParts): string {
  // 貼り付け先の contentEditable へそのまま挿入され得るため、実行され得る scheme
  // （`javascript` スキームなど）を渡さない。キャンバス画面からの呼び出しでは常に http(s) になる。
  let protocol: string;
  try {
    protocol = new URL(destination).protocol;
  } catch {
    // 相対 URL など、URL として解釈できない文字列。Markdown だけを返せるよう例外は伝播させない。
    return "";
  }
  if (protocol !== "http:" && protocol !== "https:") {
    return "";
  }
  // 補間する値は直前の 2 つの関数で実体参照化済みであり、HTML として解釈され得る文字は残らない。
  // 宛先の scheme も上で http / https に限定しているため、XSS の経路にはならない。
  // nosemgrep: tmp.security-work.sast.rules.javascript.lang.security.html-in-template-string
  return `<a href="${escapeHtmlAttribute(destination)}">${escapeHtmlText(label)}</a>`;
}

/**
 * クリップボードへ併記する 2 形式（Markdown とアンカー 1 個の HTML）を組み立てる。
 *
 * Markdown を解釈する貼り付け先と、リッチテキストを解釈する貼り付け先の両方で、
 * 同じ表示文言が同じ宛先のリンクになるようにするため、両形式を同じ中間値から作る。
 *
 * 例: `[動作確認キャンバス｜Connect Canvas](https://example.invalid/c/abc)`
 *
 * @param input 表示中の絶対 URL と、正規化済みのキャンバス名
 * @returns `text/plain` 用の Markdown と `text/html` 用の断片
 */
export function buildCanvasLinkClipboardPayload(
  input: CanvasLinkInput
): CanvasLinkClipboardPayload {
  const parts = buildCanvasLinkParts(input);
  return { html: formatAnchorHtml(parts), markdown: formatMarkdownLink(parts) };
}
